import type { SiteConfig } from "@cmp/shared";

const CACHE_KEY = "cmp_config_cache_v1";
const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry {
  site_id: string;
  ts: number;
  config: SiteConfig;
}

/**
 * Narrow runtime check. The authoritative zod schema lives in `@cmp/shared`
 * and is used by the API and admin at wire boundaries. Duplicating it here
 * would pull zod (~60 KB min) into the loader bundle, so we do a structural
 * sanity check instead — the server already validated before sending.
 */
function looksLikeSiteConfig(v: unknown): v is SiteConfig {
  if (!v || typeof v !== "object") return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c["site_id"] === "string" &&
    typeof c["domain"] === "string" &&
    typeof c["brand_name"] === "string" &&
    typeof c["privacy_policy_url"] === "string" &&
    typeof c["banner_version"] === "number" &&
    typeof c["policy_version"] === "number" &&
    typeof c["region_mode"] === "string" &&
    typeof c["accent_color"] === "string" &&
    typeof c["i18n"] === "object"
  );
}

function readCache(siteId: string): SiteConfig | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (entry.site_id !== siteId) return null;
    if (Date.now() - entry.ts > CACHE_TTL_MS) return null;
    return looksLikeSiteConfig(entry.config) ? entry.config : null;
  } catch {
    return null;
  }
}

function writeCache(config: SiteConfig): void {
  try {
    const entry: CacheEntry = { site_id: config.site_id, ts: Date.now(), config };
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* storage disabled; ignore */
  }
}

export async function fetchConfig(apiBase: string, siteId: string): Promise<SiteConfig> {
  const cached = readCache(siteId);
  if (cached) return cached;

  const res = await fetch(`${apiBase}/sites/${encodeURIComponent(siteId)}/config`, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  });
  if (!res.ok) throw new Error(`config fetch failed: ${res.status}`);
  const json: unknown = await res.json();
  if (!looksLikeSiteConfig(json)) throw new Error("config: malformed response");
  writeCache(json);
  return json;
}
